import { useCallback } from 'react';
import { useApolloClient } from '@apollo/client';

import {
  MailContactFieldsFragment,
  MailContactsPageDocument,
  MailContactsPageQuery,
  MailContactsPageQueryVariables,
  useMailContactSuggestListQuery,
} from 'core/graphql';
import { useDebouncedValue } from 'core/hooks/Meta/useDebouncedValue';

import { CursorFetcher, useCursorList } from './useCursorList';
import { IMailAddress, MailContactKind } from './mailModel';

export type IMailContact = MailContactFieldsFragment;

export const MAIL_CONTACTS_PAGE = 50;

/**
 * ТЗ 0122 — контакты моих ящиков (поиск, тип), курсором мимо кеша Apollo.
 */
export const useMailContacts = (search: string, kind: MailContactKind | '') => {
  const client = useApolloClient();
  const query = useDebouncedValue(search.trim(), 300);
  const key = JSON.stringify([query, kind || null]);
  const fetcher = useCallback<CursorFetcher<IMailContact>>(
    async (cursor, limit) => {
      const [q, k] = JSON.parse(key);
      const { data } = await client.query<
        MailContactsPageQuery,
        MailContactsPageQueryVariables
      >({
        query: MailContactsPageDocument,
        variables: { search: q || null, kind: k, cursor, limit },
        fetchPolicy: 'no-cache',
      });
      return {
        items: data.mailContacts.items,
        nextCursor: data.mailContacts.nextCursor ?? null,
        total: data.mailContacts.total ?? null,
      };
    },
    [client, key],
  );
  return useCursorList(key, fetcher, MAIL_CONTACTS_PAGE);
};

const NO_SUGGESTIONS: IMailAddress[] = [];

/** Автодополнение адресатов (контакты моих ящиков + сотрудники). */
export const useMailContactSuggest = (text: string) => {
  const query = useDebouncedValue(text.trim(), 250);
  const { data, loading } = useMailContactSuggestListQuery({
    variables: { query, limit: 8 },
    skip: query.length < 1,
    fetchPolicy: 'cache-first',
  });
  return {
    suggestions:
      query.length < 1
        ? NO_SUGGESTIONS
        : data?.mailContactSuggest ?? NO_SUGGESTIONS,
    loading,
    settled: query === text.trim(),
  };
};
